import React, { useEffect, useRef, useState } from "react";
import { Music, RefreshCw, Upload, Loader2, Play, Pause, BadgeCheck, Lock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Track = ({ t, onChange, onRemove }) => {
  const [busy, setBusy] = useState("");
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  const file = useRef(null);

  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); } else { a.play(); setPlaying(true); }
  };
  const regen = async () => {
    setBusy("regen");
    try { const r = await api.post(`/admin/music/${t.id}/regenerate`); onChange(r.data.track); toast.success(`تم توليد «${t.name}»`); }
    catch (e) { toast.error(e.response?.data?.detail || "تعذّر التوليد"); }
    finally { setBusy(""); }
  };
  const approve = async (flag) => {
    setBusy("approve");
    try { const r = await api.put(`/admin/music/${t.id}/approve`, { approved: flag }); onChange(r.data.track); toast.success(flag ? `تم اعتماد «${t.name}» — لن يتغير إلا من الأدمن` : `أُلغي اعتماد «${t.name}»`); }
    catch (e) { toast.error(e.response?.data?.detail || "تعذّر الاعتماد"); }
    finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm(`حذف المقطع الإضافي «${t.name}»؟`)) return;
    setBusy("remove");
    try { await api.delete(`/admin/music/${t.id}`); onRemove(t.id); toast.success("تم الحذف"); }
    catch (e) { toast.error(e.response?.data?.detail || "تعذّر الحذف"); }
    finally { setBusy(""); }
  };
  const upload = async (f) => {
    if (!f) return;
    setBusy("upload");
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post(`/admin/music/${t.id}/upload`, fd); onChange(r.data.track); toast.success(`تم استبدال «${t.name}»`); }
    catch (e) { toast.error(e.response?.data?.detail || "تعذّر الرفع"); }
    finally { setBusy(""); }
  };

  return (
    <div className={`rounded-xl border p-3 flex items-center gap-3 flex-wrap ${t.approved ? "border-emerald-500/60 bg-emerald-500/5" : "border-border bg-secondary/30"}`} data-testid={`music-track-${t.id}`}>
      <button onClick={toggle} disabled={!t.has_file} data-testid={`music-play-${t.id}`}
        className="w-10 h-10 shrink-0 rounded-full bg-primary/15 text-primary flex items-center justify-center disabled:opacity-30 hover:bg-primary/25 transition-colors">
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      {t.has_file && <audio ref={audio} src={`${API}/admin/music/${t.id}/audio?v=${t.updated_at}`} onEnded={() => setPlaying(false)} preload="none" />}
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm flex items-center gap-1.5">{t.name}{t.approved && <BadgeCheck className="w-4 h-4 text-emerald-500" data-testid={`music-approved-${t.id}`} />}{t.custom && <span className="text-[10px] font-normal text-muted-foreground">إضافي</span>}</div>
        <div className="text-[11px] text-muted-foreground truncate">{t.has_file ? `${Math.round(t.size / 1024)} KB` : "لم يُولَّد بعد"}{t.keys?.length ? ` · ${t.keys.slice(0, 4).join("، ")}` : ""}</div>
      </div>
      {t.approved ? (
        <Button size="sm" variant="outline" onClick={() => approve(false)} disabled={!!busy} className="h-8 gap-1 border-emerald-500/50 text-emerald-600" data-testid={`music-unapprove-${t.id}`}>
          {busy === "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />} معتمد — إلغاء القفل
        </Button>
      ) : (
        <>
          {t.has_file && (
            <Button size="sm" onClick={() => approve(true)} disabled={!!busy} className="h-8 gap-1 bg-emerald-600 hover:bg-emerald-600/90 text-white" data-testid={`music-approve-${t.id}`}>
              {busy === "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <BadgeCheck className="w-3 h-3" />} اعتماد
            </Button>
          )}
          {!t.custom && (
            <Button size="sm" variant="outline" onClick={regen} disabled={!!busy} className="h-8 gap-1 border-border" data-testid={`music-regen-${t.id}`}>
              {busy === "regen" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} توليد
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => file.current?.click()} disabled={!!busy} className="h-8 gap-1 border-border" data-testid={`music-upload-${t.id}`}>
            {busy === "upload" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} رفع mp3
          </Button>
          {t.custom && (
            <Button size="sm" variant="ghost" onClick={remove} disabled={!!busy} className="h-8 text-destructive" data-testid={`music-delete-${t.id}`}><Trash2 className="w-3 h-3" /></Button>
          )}
        </>
      )}
      <input ref={file} type="file" accept="audio/mpeg,.mp3" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
};

const AddTrack = ({ onAdded }) => {
  const [name, setName] = useState("");
  const [keys, setKeys] = useState("");
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const submit = async () => {
    const f = file.current?.files?.[0];
    if (!name.trim() || !f) { toast.error("أدخل العنوان واختر ملف mp3"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("name", name.trim()); fd.append("keys", keys); fd.append("file", f);
    try { const r = await api.post("/admin/music/custom", fd); onAdded(r.data.track); setName(""); setKeys(""); file.current.value = ""; toast.success(`أُضيف «${r.data.track.name}»`); }
    catch (e) { toast.error(e.response?.data?.detail || "تعذّرت الإضافة"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-dashed border-border p-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto] items-center" data-testid="music-add">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="عنوان المقطع (مثل: وطني حماسي)" data-testid="music-add-name"
        className="h-9 rounded-lg bg-secondary border border-border px-3 text-sm outline-none focus:border-primary" />
      <input value={keys} onChange={(e) => setKeys(e.target.value)} placeholder="كلمات المزاج مفصولة بفواصل (وطني، حماس، اليوم الوطني)" data-testid="music-add-keys"
        className="h-9 rounded-lg bg-secondary border border-border px-3 text-sm outline-none focus:border-primary" />
      <input ref={file} type="file" accept="audio/mpeg,.mp3" className="text-xs max-w-[160px]" data-testid="music-add-file" />
      <Button size="sm" onClick={submit} disabled={busy} className="h-9 gap-1" data-testid="music-add-submit">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} إضافة مقطع</Button>
    </div>
  );
};

export const AdminMusic = () => {
  const [tracks, setTracks] = useState(null);
  const load = async () => {
    try { const r = await api.get("/admin/music"); setTracks(r.data.tracks); }
    catch { toast.error("تعذّر تحميل مكتبة الموسيقى"); }
  };
  useEffect(() => { load(); }, []);
  const ready = (tracks || []).filter((t) => t.has_file).length;

  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="admin-music">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-extrabold font-heading flex items-center gap-2"><Music className="w-5 h-5 text-primary" /> مكتبة الموسيقى الخليجية</h2>
          <p className="text-xs text-muted-foreground mt-1">كل الموسيقى تُدار من هنا: ارفع ثم <b className="text-foreground">اعتمد</b> — المعتمد لا يتغير إلا من الأدمن، ويُختار المقطع تلقائيًا حسب عنوانه وكلمات مزاجه واحتياج الفيديو. جاهز: <b className="text-foreground" data-testid="music-ready-count">{ready}/{(tracks || []).length}</b></p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1 border-border" data-testid="music-refresh"><RefreshCw className="w-3 h-3" /> تحديث</Button>
      </div>
      {!tracks ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {tracks.map((t) => <Track key={t.id} t={t} onChange={(nt) => setTracks((xs) => xs.map((x) => (x.id === nt.id ? nt : x)))} onRemove={(id) => setTracks((xs) => xs.filter((x) => x.id !== id))} />)}
        </div>
      )}
      <AddTrack onAdded={(nt) => setTracks((xs) => [...(xs || []), nt])} />
    </section>
  );
};
